import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import { inp } from "./e360Primitives";

// Consultant edit-before-finalise: bounded overrides for Benefits / Change / RAID.
export default function ReportOverridesEditor({ clientId, sections, onSaved }) {
  const benefitRows = (sections?.benefits?.rows) || [];
  const raidEntries = (sections?.raid?.entries) || [];
  const [owners, setOwners] = useState({});
  const [benefitsNote, setBenefitsNote] = useState("");
  const [changeNote, setChangeNote] = useState("");
  const [stakeAdds, setStakeAdds] = useState([]);
  const [raidAdds, setRaidAdds] = useState([]);
  const [hide, setHide] = useState({});
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    api.get(`/admin/enterprise360/clients/${clientId}/report-overrides`).then(({ data }) => {
      const ov = data.report_overrides || {};
      setOwners((ov.benefits && ov.benefits.owners) || {});
      setBenefitsNote((ov.benefits && ov.benefits.note) || "");
      setChangeNote((ov.change_adoption && ov.change_adoption.note) || "");
      setStakeAdds((ov.change_adoption && ov.change_adoption.stakeholders_add) || []);
      setRaidAdds((ov.raid && ov.raid.add) || []);
      const h = {}; ((ov.raid && ov.raid.hide) || []).forEach((r) => { h[r] = true; });
      setHide(h);
    }).catch(() => {});
  }, [clientId]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        benefits: { owners, note: benefitsNote },
        change_adoption: { note: changeNote, stakeholders_add: stakeAdds.filter((s) => (s.role || "").trim()) },
        raid: { add: raidAdds.filter((r) => (r.item || "").trim()), hide: Object.keys(hide).filter((k) => hide[k]) },
      };
      await api.put(`/admin/enterprise360/clients/${clientId}/report-overrides`, payload);
      toast.success("Report overrides saved — regenerate the Kit/deck to apply.");
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save overrides"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-zinc-200 rounded-lg p-4 my-4" data-testid="e360-overrides-editor">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left">
        <span className="text-[12px] tracking-[0.14em] uppercase text-[var(--e360-primary)] font-semibold">
          Edit report content (Change · Benefits · RAID)
        </span>
        <span className="text-zinc-400 text-xs">{open ? "Hide" : "Edit"}</span>
      </button>

      {open && (
        <div className="mt-4 space-y-5">
          {/* Benefits owners */}
          <div>
            <div className="text-[11px] tracking-[0.14em] uppercase text-zinc-500 mb-1">Benefit owners</div>
            <div className="space-y-1">
              {benefitRows.map((b, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-zinc-600 flex-1 truncate" title={b.benefit}>{b.benefit}</span>
                  <input data-testid={`e360-ov-owner-${i}`} className={`${inp} w-56`} placeholder={b.owner}
                    value={owners[i] || ""} onChange={(e) => setOwners({ ...owners, [i]: e.target.value })} />
                </div>
              ))}
            </div>
            <textarea data-testid="e360-ov-benefits-note" className={`${inp} mt-2`} rows={2}
              placeholder="Benefits note (shown in report & tracker)…" value={benefitsNote}
              onChange={(e) => setBenefitsNote(e.target.value)} />
          </div>

          {/* Change stakeholders */}
          <div>
            <div className="text-[11px] tracking-[0.14em] uppercase text-zinc-500 mb-1 flex items-center justify-between">
              Add stakeholders
              <button onClick={() => setStakeAdds([...stakeAdds, { role: "", streams: 1, influence: "Medium", approach: "" }])}
                className="text-[var(--e360-primary)] flex items-center gap-1 text-xs"><Plus size={13} /> Add</button>
            </div>
            {stakeAdds.map((s, i) => (
              <div key={i} className="flex items-center gap-2 mb-1">
                <input className={`${inp} flex-1`} placeholder="Role / name" value={s.role}
                  onChange={(e) => setStakeAdds(stakeAdds.map((x, ix) => ix === i ? { ...x, role: e.target.value } : x))} />
                <input className={`${inp} w-40`} placeholder="Engagement approach" value={s.approach}
                  onChange={(e) => setStakeAdds(stakeAdds.map((x, ix) => ix === i ? { ...x, approach: e.target.value } : x))} />
                <button onClick={() => setStakeAdds(stakeAdds.filter((_, ix) => ix !== i))} className="text-zinc-400 hover:text-red-500"><Trash2 size={14} /></button>
              </div>
            ))}
            <textarea data-testid="e360-ov-change-note" className={`${inp} mt-1`} rows={2}
              placeholder="Change & adoption note…" value={changeNote} onChange={(e) => setChangeNote(e.target.value)} />
          </div>

          {/* RAID add + hide */}
          <div>
            <div className="text-[11px] tracking-[0.14em] uppercase text-zinc-500 mb-1 flex items-center justify-between">
              Add RAID entries
              <button onClick={() => setRaidAdds([...raidAdds, { category: "Risk", item: "", owner: "", action: "" }])}
                className="text-[var(--e360-primary)] flex items-center gap-1 text-xs"><Plus size={13} /> Add</button>
            </div>
            {raidAdds.map((r, i) => (
              <div key={i} className="flex items-center gap-2 mb-1">
                <select className={`${inp} w-28`} value={r.category}
                  onChange={(e) => setRaidAdds(raidAdds.map((x, ix) => ix === i ? { ...x, category: e.target.value } : x))}>
                  {["Risk", "Assumption", "Issue", "Dependency"].map((o) => <option key={o}>{o}</option>)}
                </select>
                <input className={`${inp} flex-1`} placeholder="Item" value={r.item}
                  onChange={(e) => setRaidAdds(raidAdds.map((x, ix) => ix === i ? { ...x, item: e.target.value } : x))} />
                <input className={`${inp} w-32`} placeholder="Owner" value={r.owner}
                  onChange={(e) => setRaidAdds(raidAdds.map((x, ix) => ix === i ? { ...x, owner: e.target.value } : x))} />
                <button onClick={() => setRaidAdds(raidAdds.filter((_, ix) => ix !== i))} className="text-zinc-400 hover:text-red-500"><Trash2 size={14} /></button>
              </div>
            ))}
            {raidEntries.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-zinc-500">Hide auto-generated RAID entries ({raidEntries.length})</summary>
                <div className="mt-1 max-h-40 overflow-auto space-y-0.5">
                  {raidEntries.map((e, i) => (
                    <label key={i} className="flex items-center gap-2 text-[11px] text-zinc-600">
                      <input type="checkbox" checked={!!hide[e.ref]} onChange={(ev) => setHide({ ...hide, [e.ref]: ev.target.checked })} />
                      <span className="text-zinc-400 w-16">{e.ref}</span>
                      <span className="truncate">{e.item}</span>
                    </label>
                  ))}
                </div>
              </details>
            )}
          </div>

          <button data-testid="e360-ov-save" onClick={save} disabled={busy}
            className="bg-[var(--e360-primary)] text-white text-[12px] tracking-[0.14em] uppercase px-4 py-2 rounded flex items-center gap-1 disabled:opacity-50">
            <Save size={14} /> {busy ? "Saving…" : "Save overrides"}
          </button>
        </div>
      )}
    </div>
  );
}
